import { ReactElement, useEffect, useRef, useState } from "react";
import { Box, styled, TableCell, TableFooter, TableRow } from "@mui/material";

import { DynamicTable } from "..";
import { getFormattedValue, numberWithCommas } from "@/utils";

export const StyledBox = styled(Box)(() => ({
  flex: 1,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  "& .MuiTable-root": {
    borderCollapse: "collapse",
    minWidth: "initial",
    tableLayout: "fixed",
    width: "100%",

    "& .MuiTableCell-root": {
      border: 0,
      fontSize: { xs: "9px", md: "10px", lg: "11px" },
    },

    "& .MuiTableHead-root .MuiTableCell-root": {
      padding: { xs: "3px 4px !important", md: "5px 6px !important" },
      fontSize: { xs: "9px", md: "10px", lg: "11px" },
      fontWeight: 500,
      whiteSpace: "normal",
      lineHeight: 1.2,
      height: "auto",
      minHeight: "32px",
      backgroundColor: "#E6ECED !important",
      color: "#030712 !important",
    },

    // First column specific styling to prevent wrapping
    "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
      whiteSpace: "nowrap",
    },

    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: { xs: "3px 4px !important", md: "5px 6px !important" },
      borderBlock: "1px solid #E6E7E7",
      fontSize: { xs: "9px", md: "10px", lg: "11px" },
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.2,
    },

    // First column specific styling in body to prevent wrapping
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
      whiteSpace: "nowrap",
      wordBreak: "keep-all",
    },

    "& .MuiTableFooter-root": {
      position: "sticky",
      bottom: 0,
      zIndex: 2,
    },
    "& .MuiTableFooter-root .MuiTableCell-root": {
      background: "#E6ECED",
      fontSize: { xs: "10px", md: "11px", lg: "12px" },
      fontWeight: 500,
      color: "#030712",
      padding: { xs: "3px 4px !important", md: "5px 6px !important" },
    },
  },
  // Custom scrollbar styling for better visibility
  "&::-webkit-scrollbar": {
    width: 4,
    height: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
}));

interface IVendorsTable<T> extends Omit<DynamicTableProps<T>, "data"> {
  footerColumns: VendorSection[];
  footerData: Record<string, number>;
  data: T[];
  fillerRowCount?: number; // number of empty rows to insert before footer
}

const VendorsTable = <T extends IBase>({
  footerColumns,
  footerData,
  data,
  fillerRowCount = 0,
  ...tableProps
}: IVendorsTable<T>): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const recomputeScrollability = () => {
    const el = containerRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 1; // allow rounding
    setIsScrollable(scrollable);
  };

  useEffect(() => {
    // recompute when data length or requested filler changes
    requestAnimationFrame(recomputeScrollability);
  }, [data?.length, fillerRowCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => recomputeScrollability());
    observer.observe(el);
    window.addEventListener("resize", recomputeScrollability);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recomputeScrollability);
    };
  }, []);

  // Cap fillers so total visible rows (data + fillers) never exceeds 5
  const maxAllowedFillers = Math.max(0, 5 - (Array.isArray(data) ? data.length : 0));
  const effectiveFillerRowCount = isScrollable
    ? 0
    : Math.min(Math.max(0, fillerRowCount), maxAllowedFillers);

  return (
    <StyledBox ref={containerRef}>
      <DynamicTable
        {...tableProps}
        data={data}
        stickyHeader
        hidePagination
        hideScrollBars={false}
        isFullHeight
        square
        borderLess
        contentIdKey={tableProps.contentIdKey}
        containerStyles={{
          overflow: "auto",
          maxWidth: "100%",
        }}
        fillerRowCount={effectiveFillerRowCount}
        // Move filler rows into DynamicTable body via prop; keep footer only for totals
        renderFooter={() => (
          <TableFooter>
            <TableRow
              sx={{
                backgroundColor: "transparent !important",
                position: "sticky",
                bottom: "0",
              }}
            >
              {footerColumns.map((section, index) => {
                const position = tableProps.columns[index].align;
                const width = tableProps.columns[index].width;
                const minWidth = tableProps.columns[index].minWidth;
                let value: string | number = "";
                let key = "";

                if (section.type === "total") {
                  key = `${section.name}-${index}`;
                  value = section.name;
                } else if (section.type === "empty") {
                  key = `empty-${index}`;
                  value = "";
                } else {
                  key = `${section.fieldName}-${index}`;
                  value = section.fieldName === "percentCustomValue" || section.fieldName === "effectiveDutyRate"
                    ? `${footerData[section.fieldName].toFixed(2)}%`
                    : getFormattedValue(
                        numberWithCommas(footerData[section.fieldName]),
                        section.symbol ?? "",
                        position
                      );
                }
                return (
                  <TableCell
                    key={`footer-${key}`}
                    align={position}
                    sx={{
                      width,
                      minWidth: minWidth || "auto",
                      backgroundColor: "#E6ECED !important",
                      color: "#030712",
                      fontSize: { xs: "10px", md: "11px", lg: "12px" },
                    }}
                  >
                    {value}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        )}
      />
    </StyledBox>
  );
};

export default VendorsTable;

